import { z } from 'zod';
/**
 * Settlement state of a B2B platform invoice.
 */
export const B2B_INVOICE_STATUSES = [
    'draft',
    'issued',
    'paid',
    'overdue',
    'in_grace',
    'void',
    'disputed',
];
export const B2BInvoiceStatusSchema = z.enum(B2B_INVOICE_STATUSES);
/**
 * GST Tax Breakdown representation (India Tax Rules).
 * SAC codes: 998313 (IT / SaaS Platform Hosting Services).
 */
export const GSTTaxBreakdownSchema = z.object({
    hsnSacCode: z.string().default('998313'),
    placeOfSupplyStateCode: z.string().length(2), // e.g. 'KA'
    supplierStateCode: z.string().length(2).default('KA'), // SenseSys entity state
    isInterState: z.boolean(),
    taxableAmountInr: z.number().int().nonnegative(),
    cgstRatePercent: z.number().nonnegative(),
    cgstAmountInr: z.number().int().nonnegative(),
    sgstRatePercent: z.number().nonnegative(),
    sgstAmountInr: z.number().int().nonnegative(),
    igstRatePercent: z.number().nonnegative(),
    igstAmountInr: z.number().int().nonnegative(),
    totalTaxAmountInr: z.number().int().nonnegative(),
    reverseChargeApplicable: z.boolean().default(false),
});
/**
 * Line item in a B2B platform subscription invoice.
 */
export const B2BInvoiceLineItemSchema = z.object({
    id: z.string(),
    description: z.string(),
    hsnSacCode: z.string().default('998313'),
    quantity: z.number().int().positive(),
    unitPriceInr: z.number().int().nonnegative(),
    amountInr: z.number().int().nonnegative(),
    discountAmountInr: z.number().int().nonnegative().default(0),
});
/**
 * B2B Platform Invoice model.
 */
export const B2BInvoiceSchema = z.object({
    id: z.string(),
    invoiceNumber: z.string(),
    organisationId: z.string(),
    organisationName: z.string().optional(),
    contractId: z.string(),
    contractNumber: z.string().optional(),
    status: B2BInvoiceStatusSchema,
    billingPeriodStart: z.iso.datetime(),
    billingPeriodEnd: z.iso.datetime(),
    issueDate: z.iso.datetime(),
    dueDate: z.iso.datetime(),
    graceUntilDate: z.iso.datetime().nullable().optional(),
    paidAt: z.iso.datetime().nullable().optional(),
    paymentReference: z.string().nullable().optional(),
    currency: z.literal('INR'),
    subtotalInr: z.number().int().nonnegative(),
    discountInr: z.number().int().nonnegative().default(0),
    gstTax: GSTTaxBreakdownSchema,
    totalAmountInr: z.number().int().nonnegative(),
    customerGstin: z.string().nullable().optional(),
    recipientEmail: z.string().email(),
    deliveryStatus: z.enum(['pending', 'delivered', 'failed', 'bounced']).default('pending'),
    lineItems: z.array(B2BInvoiceLineItemSchema),
    notes: z.string().nullable().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int().min(1),
});
/**
 * Invoice creation payload.
 */
export const B2BInvoiceCreateSchema = z
    .object({
    organisationId: z.string(),
    contractId: z.string(),
    billingPeriodStart: z.iso.datetime(),
    billingPeriodEnd: z.iso.datetime(),
    dueDate: z.iso.datetime(),
    subtotalInr: z.number().int().nonnegative(),
    discountInr: z.number().int().nonnegative().default(0),
    placeOfSupplyStateCode: z.string().length(2).default('KA'),
    customerGstin: z.string().max(20).optional(),
    recipientEmail: z.string().email(),
    lineItems: z
        .array(z.object({
        description: z.string().min(3),
        quantity: z.number().int().positive().default(1),
        unitPriceInr: z.number().int().nonnegative(),
        discountAmountInr: z.number().int().nonnegative().default(0),
    }))
        // A tax invoice with no line items is not an invoice.
        .min(1),
    notes: z.string().max(1000).optional(),
})
    .strict();
/**
 * Record settlement (mark paid / record payment reference).
 */
export const RecordSettlementSchema = z
    .object({
    paymentReference: z.string().trim().min(3).max(100),
    paidAt: z.iso.datetime().optional(),
    expectedVersion: z.number().int().min(1),
})
    .strict();
//# sourceMappingURL=billing.js.map